// Wizard configuration mirrors backend wizard_meta.py.
// Each step is either a scalar form (updates doctor_profiles) or a list (doctor_profile_items with a kind).

export const WIZARD_STEPS = [
    {
        n: 1,
        key: "personal_info",
        title: "Personal Info",
        required: true,
        kind: "scalar",
        description:
            "Basic personal details — used on your profile header. Never shown publicly beyond what you approve.",
        fields: [
            { name: "gender", label: "Gender", type: "select", options: ["Female", "Male", "Non-binary", "Prefer not to say"], required: true },
            { name: "date_of_birth", label: "Date of birth", type: "date" },
            { name: "languages", label: "Languages spoken", type: "chips", placeholder: "English, Hindi, …" },
            { name: "profile_photo_url", label: "Profile photo", type: "upload", category: "profile_photo" },
        ],
    },
    {
        n: 2,
        key: "professional_info",
        title: "Professional Info",
        required: true,
        kind: "scalar",
        description: "Your registration and years of practice.",
        fields: [
            { name: "registration_council", label: "Registration council", type: "text", required: true, placeholder: "e.g. Karnataka Medical Council" },
            { name: "registration_year", label: "Registration year", type: "number", required: true, min: 1950, max: 2030 },
            { name: "years_of_experience", label: "Years of experience", type: "number", required: true, min: 0, max: 70 },
            { name: "primary_qualifications", label: "Primary qualifications", type: "chips", placeholder: "MBBS, MD, DM …" },
        ],
    },
    { n: 3, key: "education", title: "Education", required: false, kind: "list", listKind: "education",
        description: "Degrees, institutions, and years.",
        itemFields: [
            { name: "degree", label: "Degree", required: true },
            { name: "institution", label: "Institution", required: true },
            { name: "year", label: "Year", type: "number", min: 1950, max: 2030 },
        ],
        summary: (p) => `${p.degree || "—"} · ${p.institution || "—"}${p.year ? " · " + p.year : ""}`,
    },
    { n: 4, key: "experience", title: "Experience", required: false, kind: "list", listKind: "experience",
        description: "Roles and organizations where you have practiced.",
        itemFields: [
            { name: "role", label: "Role", required: true },
            { name: "organization", label: "Organization", required: true },
            { name: "from_year", label: "From year", type: "number", min: 1950, max: 2030 },
            { name: "to_year", label: "To year", type: "number", min: 1950, max: 2030 },
            { name: "description", label: "Short description", type: "textarea" },
        ],
        summary: (p) => `${p.role || "—"} at ${p.organization || "—"}${p.from_year ? " · " + p.from_year : ""}${p.to_year ? "–" + p.to_year : ""}`,
    },
    {
        n: 5,
        key: "specialization",
        title: "Specialization",
        required: true,
        kind: "scalar",
        description: "Your core focus. The AI uses this to shape your specialization description.",
        fields: [
            { name: "primary_specialization", label: "Primary specialization", type: "text", required: true },
            { name: "sub_specializations", label: "Sub-specializations", type: "chips" },
            { name: "areas_of_interest", label: "Areas of interest", type: "chips" },
            { name: "procedures_offered", label: "Procedures offered", type: "chips" },
        ],
    },
    { n: 6, key: "clinic_info", title: "Clinic Info", required: true, kind: "list", listKind: "clinic",
        description: "Add every clinic where you consult.",
        itemFields: [
            { name: "clinic_name", label: "Clinic name", required: true },
            { name: "address", label: "Address", type: "textarea", required: true },
            { name: "city", label: "City", required: true },
            { name: "state", label: "State" },
            { name: "country", label: "Country" },
            { name: "phone", label: "Phone" },
            { name: "google_maps_link", label: "Google Maps link" },
        ],
        summary: (p) => `${p.clinic_name || "—"} · ${p.city || "—"}`,
    },
    { n: 7, key: "hospital_affiliations", title: "Hospital Affiliations", required: false, kind: "list", listKind: "hospital_affiliation",
        description: "Hospitals where you visit or hold privileges.",
        itemFields: [
            { name: "hospital_name", label: "Hospital", required: true },
            { name: "role", label: "Role" },
            { name: "from_year", label: "From year", type: "number" },
            { name: "to_year", label: "To year", type: "number" },
        ],
        summary: (p) => `${p.hospital_name || "—"}${p.role ? " · " + p.role : ""}`,
    },
    {
        n: 8,
        key: "consultation_info",
        title: "Consultation Info",
        required: true,
        kind: "scalar",
        description: "Hours, types, and fees.",
        fields: [
            { name: "consultation_hours", label: "Consultation hours", type: "hours", required: true },
            { name: "consultation_types", label: "Consultation types", type: "chips", placeholder: "In-person, Telehealth …" },
            { name: "base_fee_inr", label: "Base consultation fee (INR)", type: "number", required: true, min: 0 },
            { name: "follow_up_fee_inr", label: "Follow-up fee (INR)", type: "number", min: 0 },
            { name: "consultation_duration_minutes", label: "Slot duration (minutes)", type: "number", min: 5, max: 120 },
            { name: "insurance_accepted", label: "Insurance accepted", type: "chips" },
        ],
        profileExtSection: {
            title: "Teleconsultation & channel links",
            description: "Optional. If provided, patients see quick-action buttons on your public page.",
            fields: [
                { name: "teleconsultation_link", label: "Teleconsultation link", type: "url", placeholder: "https://your-tele-provider/…", maxLength: 500 },
                { name: "telegram_channel_link", label: "Telegram channel link", type: "url", placeholder: "https://t.me/yourhandle", maxLength: 500 },
            ],
        },
    },
    { n: 9, key: "achievements", title: "Achievements", required: false, kind: "list", listKind: "achievement",
        itemFields: [
            { name: "title", label: "Title", required: true },
            { name: "year", label: "Year", type: "number" },
            { name: "description", label: "Description", type: "textarea" },
        ],
        summary: (p) => `${p.title || "—"}${p.year ? " · " + p.year : ""}`,
    },
    { n: 10, key: "awards", title: "Awards", required: false, kind: "list", listKind: "award",
        itemFields: [
            { name: "title", label: "Award", required: true },
            { name: "awarding_body", label: "Awarding body" },
            { name: "year", label: "Year", type: "number" },
        ],
        summary: (p) => `${p.title || "—"}${p.awarding_body ? " · " + p.awarding_body : ""}`,
    },
    { n: 11, key: "publications", title: "Publications", required: false, kind: "list", listKind: "publication",
        itemFields: [
            { name: "title", label: "Title", required: true },
            { name: "journal", label: "Journal" },
            { name: "year", label: "Year", type: "number" },
            { name: "link", label: "Link" },
        ],
        summary: (p) => `${p.title || "—"}${p.journal ? " · " + p.journal : ""}`,
    },
    { n: 12, key: "memberships", title: "Memberships", required: false, kind: "list", listKind: "membership",
        itemFields: [
            { name: "organization", label: "Organization", required: true },
            { name: "position", label: "Position / role" },
            { name: "year", label: "Year", type: "number" },
        ],
        summary: (p) => `${p.organization || "—"}${p.position ? " · " + p.position : ""}`,
    },
    { n: 13, key: "social_media", title: "Social Media", required: false, kind: "list", listKind: "social_link",
        itemFields: [
            { name: "platform", label: "Platform", type: "select", options: ["Instagram", "LinkedIn", "YouTube", "Twitter/X", "Facebook", "Website"], required: true },
            { name: "url", label: "URL", required: true },
        ],
        summary: (p) => `${p.platform || "—"} · ${p.url || "—"}`,
    },
    {
        n: 14,
        key: "patient_info",
        title: "Patient Info",
        required: false,
        kind: "scalar",
        description: "Guidance for patients before, during, and after their visit.",
        fields: [
            { name: "patient_info.what_to_expect", label: "What to expect", type: "textarea" },
            { name: "patient_info.what_to_bring", label: "What to bring", type: "textarea" },
            { name: "patient_info.covid_protocol", label: "COVID / infection protocol", type: "textarea" },
        ],
    },
    { n: 15, key: "health_education", title: "Health Education", required: false, kind: "list", listKind: "health_education",
        description: "Educational content pieces you own or endorse.",
        itemFields: [
            { name: "title", label: "Title", required: true },
            { name: "category", label: "Category" },
            { name: "description", label: "Short description", type: "textarea" },
            { name: "link", label: "Link" },
        ],
        summary: (p) => `${p.title || "—"}${p.category ? " · " + p.category : ""}`,
    },
    { n: 16, key: "images_videos", title: "Images & Videos", required: false, kind: "uploads",
        description: "Clinic photos and intro video. Images: JPG/PNG/WEBP ≤5MB. Video: MP4/WEBM ≤50MB.",
        categories: [
            { key: "clinic_photo", label: "Clinic photos", kind: "image", multiple: true },
            { key: "cover_image", label: "Cover image", kind: "image", multiple: false, profileField: "cover_image_url" },
            { key: "intro_video", label: "Intro video", kind: "video", multiple: false, profileField: "intro_video_url" },
        ],
    },
    { n: 17, key: "documents", title: "Documents", required: false, kind: "uploads",
        description: "CV, brochures, consent forms. PDF only, ≤10MB.",
        categories: [
            { key: "cv", label: "CV / Resume", kind: "document", multiple: false },
            { key: "brochure", label: "Brochure", kind: "document", multiple: false },
            { key: "consent_form", label: "Consent form", kind: "document", multiple: true },
        ],
    },
    {
        n: 18,
        key: "biography",
        title: "Biography",
        required: true,
        kind: "scalar",
        description: "Raw notes only — the AI will refine this into your professional biography. Facts only — do NOT include awards or credentials you didn't list earlier.",
        fields: [
            { name: "biography_notes", label: "Biography notes (raw)", type: "longtext", required: true, rows: 10, placeholder: "Practice focus, patient philosophy, notable projects, community work…" },
        ],
        profileExtSection: {
            title: "Practice philosophy & mission",
            description: "Optional narrative that renders alongside your biography on the public page.",
            fields: [
                { name: "practice_philosophy", label: "Practice philosophy", type: "textarea", rows: 4, placeholder: "Evidence, empathy, time — in that order.", maxLength: 3000 },
                { name: "mission_vision", label: "Mission & vision", type: "textarea", rows: 4, placeholder: "Deliver world-class care that respects each patient.", maxLength: 3000 },
            ],
        },
    },
    {
        n: 19,
        key: "seo_info",
        title: "SEO Info",
        required: false,
        kind: "scalar",
        description: "Optional hints — the AI will still generate SEO title + meta on its own, grounded in your input.",
        fields: [
            { name: "seo_keywords", label: "Preferred keywords", type: "chips" },
            { name: "seo_tagline", label: "Tagline (optional)", type: "text" },
        ],
        profileExtSection: {
            title: "Nominee access (optional)",
            description: "Name a nominee who can request read-only access to your Digital Identity if you become incapacitated. Their contact stays PRIVATE (not shown on your public page).",
            fields: [
                { name: "nominee_name", label: "Nominee name", type: "text", maxLength: 200 },
                { name: "nominee_relation", label: "Relation", type: "text", placeholder: "Spouse / Sibling / Colleague", maxLength: 100 },
                { name: "nominee_contact", label: "Contact (phone or email)", type: "text", maxLength: 200 },
            ],
        },
    },
    { n: 20, key: "final_review", title: "Final Review", required: false, kind: "review" },
];

export const WIZARD_TOTAL = WIZARD_STEPS.length;
